export const BOON_PERK_IDS = [
  'circle_of_healing',
  'shadow_step',
  'exponential',
  'dark_theory',
  'boon_illumination'
]

export const TOTEM_FINDER_PERK_IDS = [
  'extrasensory_perception',
  'detectives_hunch',
  'small_game',
  'clairvoyance'
]

export const BEST_BUILD_CONFLICTS = [
  {
    perks: ['open_handed', 'treacherous_crows'],
    reason: 'Open-Handed does not meaningfully carry Invocation: Treacherous Crows as a best-build pair.'
  },
  {
    perks: ['five_moves_ahead', 'windows_of_opportunity'],
    reason: 'Five Moves Ahead and Windows of Opportunity overlap too much as pallet/window routing info for a best-build slot.'
  },
  {
    perks: ['extrasensory_perception', 'eyes_of_belmont'],
    reason: 'Eyes of Belmont does not extend Extrasensory Perception because its aura reading is continuous rather than a timed reveal.'
  },
  {
    perks: ['extrasensory_perception', 'still_sight'],
    reason: 'Extrasensory Perception already covers Still Sight\'s stationary reconnaissance job with broader information.'
  },
  {
    perks: ['extrasensory_perception', 'clairvoyance'],
    reason: 'Extrasensory Perception already covers Clairvoyance\'s environmental reconnaissance without consuming a second best-build slot.'
  },
  {
    perks: ['dead_hard', 'blood_rush'],
    reason: 'Blood Rush is not a clean Dead Hard package because their health-state and exhaustion timing fight each other.'
  },
  {
    perks: ['dramaturgy', 'built_to_last'],
    reason: 'Dramaturgy does not create a reliable brought-item economy for Built to Last.'
  },
  {
    perks: ['dramaturgy', 'streetwise'],
    reason: 'Dramaturgy random item value is not stable enough to justify Streetwise in a best-build package.'
  },
  {
    perks: ['dramaturgy', 'ace_in_the_hole'],
    reason: 'Dramaturgy does not loot Chests, so Ace in the Hole does not support its main value.'
  },
  {
    perks: ['dramaturgy', 'appraisal'],
    reason: 'Dramaturgy random item value does not justify spending a best-build slot on chest rummaging.'
  },
  ...BOON_PERK_IDS.map(boonPerkId => ({
    perks: ['counterforce', boonPerkId],
    reason: 'Counterforce consumes Totems, so it fights against boon builds that want to preserve and bless them.'
  }))
]

const DRAMATURGY_FORBIDDEN_PERKS = [
  'ace_in_the_hole',
  'appraisal',
  'background_player',
  'balanced_landing',
  'blood_rush',
  'built_to_last',
  'dead_hard',
  'head_on',
  'lithe',
  'overcome',
  'smash_hit',
  'sprint_burst',
  'streetwise'
]

export const survivorBestBuildPlans = {
  sprint_burst: {
    defaultVariant: 'exhaustion_control',
    variants: [
      {
        id: 'exhaustion_control',
        title: 'Exhaustion Control',
        score: 100,
        summary: 'Bank Sprint Burst with walking mobility, recover Exhaustion faster and convert the burst into a safe route.',
        forbiddenPerks: [
          'background_player',
          'balanced_landing',
          'cut_loose',
          'dance_with_me',
          'dead_hard',
          'dramaturgy',
          'head_on',
          'lithe',
          'overcome',
          'quick_and_quiet',
          'smash_hit'
        ],
        slots: [
          {
            id: 'walking_control',
            label: 'Walking control',
            options: [
              { perkId: 'fixated', score: 100 },
              { perkId: 'vigil', score: 86 },
              { perkId: 'windows_of_opportunity', score: 78 }
            ]
          },
          {
            id: 'exhaustion_recovery',
            label: 'Exhaustion recovery',
            options: [
              { perkId: 'vigil', score: 98 },
              { perkId: 'ghost_notes', score: 84 },
              { perkId: 'blood_rush', score: 78 }
            ]
          },
          {
            id: 'route_safety',
            label: 'Route safety',
            options: [
              { perkId: 'windows_of_opportunity', score: 94 },
              { perkId: 'deja_vu', score: 70 },
              { perkId: 'resilience', score: 68 }
            ]
          }
        ]
      }
    ]
  },
  eyes_of_belmont: {
    defaultVariant: 'generator_sight',
    variants: [
      {
        id: 'generator_sight',
        title: 'Generator Sight',
        score: 100,
        summary: 'Create repeatable timed Killer Aura reveals with Great Skill Checks, then turn the same trigger into generator progress.',
        forbiddenPerks: [
          'aftercare',
          'blood_pact',
          'bond',
          'deja_vu',
          'detectives_hunch',
          'empathy',
          'extrasensory_perception',
          'kindred',
          'open_handed',
          'still_sight',
          'windows_of_opportunity',
          'wiretap'
        ],
        slots: [
          {
            id: 'timed_killer_aura',
            label: 'Timed Killer aura',
            options: [
              { perkId: 'fogwise', score: 100 },
              { perkId: 'scene_partner', score: 96 },
              { perkId: 'alert', score: 94 },
              { perkId: 'dark_sense', score: 92 }
            ]
          },
          {
            id: 'trigger_reliability',
            label: 'Trigger reliability',
            options: [
              { perkId: 'stake_out', score: 100 },
              { perkId: 'hyperfocus', score: 86 },
              { perkId: 'resilience', score: 72 }
            ]
          },
          {
            id: 'generator_conversion',
            label: 'Generator conversion',
            options: [
              { perkId: 'hyperfocus', score: 100 },
              { perkId: 'resilience', score: 78 },
              { perkId: 'prove_thyself', score: 74 }
            ]
          }
        ]
      }
    ]
  },
  balanced_landing: {
    defaultVariant: 'vertical_route',
    variants: [
      {
        id: 'vertical_route',
        title: 'Vertical Route',
        score: 94,
        summary: 'Route around drops and keep the exhaustion cycle available without stacking redundant pallet/window info.',
        forbiddenPerks: ['five_moves_ahead'],
        slots: [
          {
            id: 'route_reading',
            label: 'Route reading',
            options: [
              { perkId: 'windows_of_opportunity', score: 96 },
              { perkId: 'troubleshooter', score: 82 },
              { perkId: 'fixated', score: 74 }
            ]
          },
          {
            id: 'cooldown',
            label: 'Cooldown',
            options: [
              { perkId: 'vigil', score: 90 },
              { perkId: 'fixated', score: 78 },
              { perkId: 'resilience', score: 68 }
            ]
          },
          {
            id: 'chase_value',
            label: 'Chase value',
            options: [
              { perkId: 'resilience', score: 82 },
              { perkId: 'finesse', score: 78 },
              { perkId: 'troubleshooter', score: 74 }
            ]
          }
        ]
      }
    ]
  },
  blood_rush: {
    defaultVariant: 'exhaustion_reset',
    variants: [
      {
        id: 'exhaustion_reset',
        title: 'Exhaustion Reset',
        score: 88,
        summary: 'Use Blood Rush to refresh a normal exhaustion chase perk, not Dead Hard.',
        forbiddenPerks: ['dead_hard'],
        slots: [
          {
            id: 'exhaustion_payoff',
            label: 'Exhaustion payoff',
            options: [
              { perkId: 'sprint_burst', score: 92 },
              { perkId: 'lithe', score: 88 },
              { perkId: 'overcome', score: 80 }
            ]
          },
          {
            id: 'unhook_safety',
            label: 'Unhook safety',
            options: [
              { perkId: 'off_the_record', score: 86 },
              { perkId: 'decisive_strike', score: 78 },
              { perkId: 'resurgence', score: 72 }
            ]
          },
          {
            id: 'cooldown_control',
            label: 'Cooldown control',
            options: [
              { perkId: 'vigil', score: 84 },
              { perkId: 'windows_of_opportunity', score: 68 },
              { perkId: 'fixated', score: 64 }
            ]
          }
        ]
      }
    ]
  },
  dead_hard: {
    defaultVariant: 'anti_tunnel_chase',
    variants: [
      {
        id: 'anti_tunnel_chase',
        title: 'Anti-Tunnel Chase',
        score: 92,
        summary: 'Keep Dead Hard as an injured anti-tunnel chase perk and avoid Blood Rush timing conflicts.',
        forbiddenPerks: ['blood_rush'],
        slots: [
          {
            id: 'protection',
            label: 'Protection',
            options: [
              { perkId: 'off_the_record', score: 90 },
              { perkId: 'decisive_strike', score: 86 },
              { perkId: 'baby_sitter', score: 70 }
            ]
          },
          {
            id: 'injured_value',
            label: 'Injured value',
            options: [
              { perkId: 'resilience', score: 84 },
              { perkId: 'made_for_this', score: 74 },
              { perkId: 'iron_will', score: 68 }
            ]
          },
          {
            id: 'route_control',
            label: 'Route control',
            options: [
              { perkId: 'windows_of_opportunity', score: 76 },
              { perkId: 'vigil', score: 70 },
              { perkId: 'lucky_break', score: 66 }
            ]
          }
        ]
      }
    ]
  },
  dramaturgy: {
    defaultVariant: 'healthy_chase',
    variants: [
      {
        id: 'healthy_chase',
        title: 'Healthy Chase',
        score: 96,
        summary: 'Use Dramaturgy as a healthy-state chase button, then keep the cooldown and routing clean.',
        forbiddenPerks: DRAMATURGY_FORBIDDEN_PERKS,
        slots: [
          {
            id: 'cooldown',
            label: 'Cooldown',
            options: [
              { perkId: 'vigil', score: 100 },
              { perkId: 'fixated', score: 74 }
            ]
          },
          {
            id: 'healthy_chase',
            label: 'Healthy chase',
            options: [
              { perkId: 'finesse', score: 98 },
              { perkId: 'windows_of_opportunity', score: 78 },
              { perkId: 'troubleshooter', score: 72 }
            ]
          },
          {
            id: 'route_control',
            label: 'Route control',
            options: [
              { perkId: 'windows_of_opportunity', score: 92 },
              { perkId: 'troubleshooter', score: 82 },
              { perkId: 'fixated', score: 76 }
            ]
          }
        ]
      },
      {
        id: 'cooldown_control',
        title: 'Cooldown Control',
        score: 88,
        summary: 'Trade a pure chase slot for smoother repositioning while keeping the Exhausted cycle central.',
        forbiddenPerks: DRAMATURGY_FORBIDDEN_PERKS,
        slots: [
          {
            id: 'cooldown',
            label: 'Cooldown',
            options: [
              { perkId: 'vigil', score: 100 },
              { perkId: 'fixated', score: 78 }
            ]
          },
          {
            id: 'routing',
            label: 'Routing',
            options: [
              { perkId: 'windows_of_opportunity', score: 88 },
              { perkId: 'troubleshooter', score: 76 },
              { perkId: 'fixated', score: 74 }
            ]
          },
          {
            id: 'healthy_value',
            label: 'Healthy value',
            options: [
              { perkId: 'finesse', score: 92 },
              { perkId: 'troubleshooter', score: 78 },
              { perkId: 'resilience', score: 60 }
            ]
          }
        ]
      }
    ]
  },
  five_moves_ahead: {
    defaultVariant: 'chase_conversion',
    variants: [
      {
        id: 'chase_conversion',
        title: 'Chase Conversion',
        score: 84,
        summary: 'Use Five Moves Ahead as your routing info, then convert that read into chase or pallet value.',
        forbiddenPerks: ['windows_of_opportunity'],
        slots: [
          {
            id: 'speed_value',
            label: 'Speed value',
            options: [
              { perkId: 'finesse', score: 88 },
              { perkId: 'lithe', score: 82 },
              { perkId: 'resilience', score: 76 }
            ]
          },
          {
            id: 'pallet_value',
            label: 'Pallet value',
            options: [
              { perkId: 'chemical_trap', score: 82 },
              { perkId: 'any_means_necessary', score: 78 },
              { perkId: 'smash_hit', score: 72 }
            ]
          },
          {
            id: 'read_conversion',
            label: 'Read conversion',
            options: [
              { perkId: 'troubleshooter', score: 74 },
              { perkId: 'fixated', score: 68 },
              { perkId: 'vigil', score: 62 }
            ]
          }
        ]
      }
    ]
  },
  extrasensory_perception: {
    defaultVariant: 'crouch_scout',
    variants: [
      {
        id: 'boon_recon',
        title: 'Boon Recon',
        score: 78,
        summary: 'Optional specialist package: find one strong boon location, maintain it efficiently, and keep a chase escape.',
        forbiddenPerks: ['counterforce'],
        slots: [
          {
            id: 'primary_boon',
            label: 'Primary boon',
            options: [
              { perkId: 'shadow_step', score: 98, note: 'Best payoff when Extrasensory finds a strong boon location.' },
              { perkId: 'circle_of_healing', score: 94 },
              { perkId: 'boon_illumination', score: 88 },
              { perkId: 'exponential', score: 84 },
              { perkId: 'dark_theory', score: 78 }
            ]
          },
          {
            id: 'boon_efficiency',
            label: 'Boon efficiency',
            options: [
              { perkId: 'flow_state', score: 92 },
              { perkId: 'urban_evasion', score: 74 }
            ]
          },
          {
            id: 'safety',
            label: 'Safety',
            options: [
              { perkId: 'sprint_burst', score: 92, note: 'Protects the time spent scouting and setting up boons.' },
              { perkId: 'lithe', score: 84 },
              { perkId: 'windows_of_opportunity', score: 82 },
              { perkId: 'lucky_break', score: 74 }
            ]
          }
        ]
      },
      {
        id: 'crouch_scout',
        title: 'Crouch Scout',
        score: 100,
        summary: 'Extend the omniscient aura sweep, preserve mobility while crouched, and keep a reliable chase escape.',
        slots: [
          {
            id: 'aura_range',
            label: 'Aura range',
            options: [
              { perkId: 'open_handed', score: 100 }
            ]
          },
          {
            id: 'crouch_mobility',
            label: 'Crouch mobility',
            options: [
              { perkId: 'urban_evasion', score: 96 }
            ]
          },
          {
            id: 'chase_safety',
            label: 'Chase safety',
            options: [
              { perkId: 'sprint_burst', score: 92 },
              { perkId: 'lithe', score: 84 },
              { perkId: 'resilience', score: 78 },
              { perkId: 'windows_of_opportunity', score: 72 }
            ]
          }
        ]
      }
    ]
  },
  plot_twist: {
    defaultVariant: 'autonomous_reset',
    variants: [
      {
        id: 'autonomous_reset',
        title: 'Autonomous Reset',
        score: 100,
        summary: 'Build around choosing when to go down, recovering safely, and punishing a greedy pickup.',
        slots: [
          {
            id: 'downed_mobility',
            label: 'Downed mobility',
            options: [
              { perkId: 'tenacity', score: 94 },
              { perkId: 'unbreakable', score: 84 }
            ]
          },
          {
            id: 'pickup_plan',
            label: 'Pickup plan',
            options: [
              { perkId: 'power_struggle', score: 90 },
              { perkId: 'flip_flop', score: 86 },
              { perkId: 'soul_guard', score: 78 }
            ]
          },
          {
            id: 'reset_value',
            label: 'Reset value',
            options: [
              { perkId: 'unbreakable', score: 88 },
              { perkId: 'lucky_break', score: 78 },
              { perkId: 'resilience', score: 72 },
              { perkId: 'soul_guard', score: 70 }
            ]
          }
        ]
      },
      {
        id: 'quiet_reset',
        title: 'Quiet Reset',
        score: 76,
        summary: 'A stealthier version, useful if you want the heal/reset without the pallet pickup package.',
        slots: [
          {
            id: 'escape_window',
            label: 'Escape window',
            options: [
              { perkId: 'lucky_break', score: 90 },
              { perkId: 'lightweight', score: 78 },
              { perkId: 'distortion', score: 70 }
            ]
          },
          {
            id: 'quiet_reset',
            label: 'Quiet reset',
            options: [
              { perkId: 'bite_the_bullet', score: 82 },
              { perkId: 'quick_and_quiet', score: 76 },
              { perkId: 'iron_will', score: 68 }
            ]
          },
          {
            id: 'insurance',
            label: 'Insurance',
            options: [
              { perkId: 'tenacity', score: 86 },
              { perkId: 'unbreakable', score: 78 },
              { perkId: 'soul_guard', score: 72 }
            ]
          }
        ]
      }
    ]
  },
  open_handed: {
    defaultVariant: 'aura_extension',
    variants: [
      {
        id: 'aura_extension',
        title: 'Aura Extension',
        score: 100,
        summary: 'Stack real aura sources that benefit from extra range. Avoid fake aura pairs that only look compatible.',
        forbiddenPerks: ['treacherous_crows'],
        slots: [
          {
            id: 'team_aura',
            label: 'Team aura',
            options: [
              { perkId: 'kindred', score: 100 },
              { perkId: 'bond', score: 84 },
              { perkId: 'empathy', score: 78 }
            ]
          },
          {
            id: 'map_read',
            label: 'Map read',
            options: [
              { perkId: 'windows_of_opportunity', score: 92 },
              { perkId: 'detectives_hunch', score: 84 },
              { perkId: 'clairvoyance', score: 78 }
            ]
          },
          {
            id: 'macro_conversion',
            label: 'Macro conversion',
            options: [
              { perkId: 'extrasensory_perception', score: 100 },
              { perkId: 'still_sight', score: 80 },
              { perkId: 'visionary', score: 78 },
              { perkId: 'plunderers_instinct', score: 76 }
            ]
          }
        ]
      }
    ]
  },
  treacherous_crows: {
    defaultVariant: 'invocation_info',
    variants: [
      {
        id: 'invocation_info',
        title: 'Invocation Info',
        score: 80,
        summary: 'Use stable team info around the invocation instead of relying on Open-Handed.',
        forbiddenPerks: ['open_handed'],
        slots: [
          {
            id: 'team_read',
            label: 'Team read',
            options: [
              { perkId: 'kindred', score: 88 },
              { perkId: 'bond', score: 78 },
              { perkId: 'dark_sense', score: 72 }
            ]
          },
          {
            id: 'objective',
            label: 'Objective',
            options: [
              { perkId: 'deja_vu', score: 82 },
              { perkId: 'fogwise', score: 76 },
              { perkId: 'better_together', score: 70 }
            ]
          },
          {
            id: 'safety',
            label: 'Safety',
            options: [
              { perkId: 'sprint_burst', score: 86 },
              { perkId: 'windows_of_opportunity', score: 80 },
              { perkId: 'distortion', score: 72 }
            ]
          }
        ]
      }
    ]
  },
  circle_of_healing: {
    defaultVariant: 'boon_reset',
    variants: [
      {
        id: 'boon_reset',
        title: 'Boon Reset',
        score: 88,
        summary: 'Two-boon reset package with a finder so the setup does not waste the early game.',
        forbiddenPerks: ['counterforce'],
        slots: [
          { id: 'finder', label: 'Totem finder', options: totemFinderOptions() },
          { id: 'second_boon', label: 'Second boon', options: boonOptions(['shadow_step', 'exponential', 'boon_illumination', 'dark_theory']) },
          { id: 'heal_value', label: 'Heal value', options: scoredOptions(['botany_knowledge', 'empathic_connection', 'well_make_it'], [86, 78, 70]) }
        ]
      }
    ]
  },
  shadow_step: {
    defaultVariant: 'boon_stealth',
    variants: [
      {
        id: 'boon_stealth',
        title: 'Boon Stealth',
        score: 88,
        summary: 'Protect a boon area and include a finder when stacking multiple boons.',
        forbiddenPerks: ['counterforce'],
        slots: [
          { id: 'finder', label: 'Totem finder', options: totemFinderOptions() },
          { id: 'second_boon', label: 'Second boon', options: boonOptions(['circle_of_healing', 'exponential', 'dark_theory', 'boon_illumination']) },
          { id: 'escape', label: 'Escape', options: scoredOptions(['lucky_break', 'lightweight', 'sprint_burst'], [86, 78, 74]) }
        ]
      }
    ]
  },
  exponential: {
    defaultVariant: 'boon_anti_slug',
    variants: [
      {
        id: 'boon_anti_slug',
        title: 'Boon Anti-Slug',
        score: 84,
        summary: 'Make the boon setup findable, then add downed-state value.',
        forbiddenPerks: ['counterforce'],
        slots: [
          { id: 'finder', label: 'Totem finder', options: totemFinderOptions() },
          { id: 'second_boon', label: 'Second boon', options: boonOptions(['shadow_step', 'circle_of_healing', 'dark_theory', 'boon_illumination']) },
          { id: 'downed_value', label: 'Downed value', options: scoredOptions(['unbreakable', 'tenacity', 'soul_guard'], [88, 82, 76]) }
        ]
      }
    ]
  },
  dark_theory: {
    defaultVariant: 'boon_haste',
    variants: [
      {
        id: 'boon_haste',
        title: 'Boon Haste',
        score: 78,
        summary: 'Keep the haste boon as part of a real boon zone instead of a standalone slot.',
        forbiddenPerks: ['counterforce'],
        slots: [
          { id: 'finder', label: 'Totem finder', options: totemFinderOptions() },
          { id: 'second_boon', label: 'Second boon', options: boonOptions(['shadow_step', 'circle_of_healing', 'exponential', 'boon_illumination']) },
          { id: 'chase', label: 'Chase value', options: scoredOptions(['windows_of_opportunity', 'lithe', 'made_for_this'], [88, 80, 70]) }
        ]
      }
    ]
  },
  boon_illumination: {
    defaultVariant: 'boon_map',
    variants: [
      {
        id: 'boon_map',
        title: 'Boon Map',
        score: 82,
        summary: 'Treat Illumination as a map-reading boon and still bring a real totem finder when stacking boons.',
        forbiddenPerks: ['counterforce'],
        slots: [
          { id: 'finder', label: 'Totem finder', options: totemFinderOptions() },
          { id: 'second_boon', label: 'Second boon', options: boonOptions(['shadow_step', 'circle_of_healing', 'exponential', 'dark_theory']) },
          { id: 'aura_value', label: 'Aura value', options: scoredOptions(['open_handed', 'detectives_hunch', 'clairvoyance'], [86, 78, 72]) }
        ]
      }
    ]
  }
}

function scoredOptions (perkIds, scores) {
  return perkIds.map((perkId, index) => ({
    perkId,
    score: scores[index] || 50
  }))
}

function boonOptions (perkIds) {
  return scoredOptions(perkIds, [92, 86, 80, 74, 68])
}

function totemFinderOptions () {
  return [
    { perkId: 'extrasensory_perception', score: 96 },
    { perkId: 'detectives_hunch', score: 86 },
    { perkId: 'small_game', score: 78 },
    { perkId: 'clairvoyance', score: 72 }
  ]
}

export default survivorBestBuildPlans
